import React, { useEffect, useState, useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Tooltip,
  IconButton,
  Typography,
  Pagination,
  Stack,
  MenuItem,
  Menu,
  Button,
  TableFooter,
  MenuItem as MuiMenuItem,
  Modal,
} from "@mui/material";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import { styled } from "@mui/system";
import { FilterList, Search } from "@mui/icons-material";
import ResetIcon from "../../../../assets/image/reseticon.svg";
import PrintIcon from "../../../../assets/image/PrintButton.svg";
import Swal from "sweetalert2";
import { useLocation, useNavigate } from "react-router-dom";
import InfoIcon from "@mui/icons-material/Info";
import { errorMsg } from "../../../../utils/toast-msg";
import moment from "moment";
import "moment-timezone";
import { useMediaQuery } from "@mui/material";
import { useTheme } from "@material-ui/core";
import * as XLSX from "xlsx";
import commaNumber from "comma-number";
import { CustomSearchInput } from "../../../../components/CustomSearchInput";
import { CustomSelect } from "../../../../components/CustomSelect";
import { CustomInput } from "../../../../components/CustomInput";
import { getHajjPackage } from "../../../../services/hajj/hajj-package";
import { successAlert } from "../../../../utils/success-alert";
import { errorAlert } from "../../../../utils/error-alert";
import CloseIcon from "@mui/icons-material/Close";
import GetAppIcon from "@mui/icons-material/GetApp";

// ---------------------------------------------------------------------------
// Styled components
// ---------------------------------------------------------------------------

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  bgcolor: "background.paper",
  boxShadow: 24,
  borderRadius: "12px",
};

const CustomTableCell = styled(TableCell)(() => ({
  borderBottom: "none",
  padding: "10px",
  fontFamily: "Outfit",
  fontSize: "11px",
  color: "var(--color-airline-code)",
  textAlign: "center",
  verticalAlign: "middle",
}));

const CustomTableHeaderCell = styled(TableCell)(({ group }) => ({
  fontWeight: 500,
  fontFamily: "Outfit",
  fontSize: "11px",
  textAlign: "center",
  verticalAlign: "middle",
  padding: "10px",
  backgroundColor:
    group === "first"
      ? "var(--color-draft)"
      : group === "second"
        ? "var(--badge-group-table-cell)"
        : "var(--badge-group-table-cell-1)",
  color:
    group === "first"
      ? "var(--color-bg-white)"
      : group === "second"
        ? "var(--color-gray)"
        : "var(--color-refund)",
}));

const CustomColoredCell = styled(CustomTableCell)(({ column }) => ({
  backgroundColor:
    column === "cashBalance"
      ? "var(--badge-group-table-cell-2)"
      : "var(--badge-group-table-cell-1)",
}));

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const serviceTypes = [
  { name: "AIR TICKET", color: "var(--color-service-air)" },
  { name: "VISA", color: "var(--color-service-visa)" },
  { name: "HOTEL", color: "var(--color-service-hotel)" },
  { name: "TRANSPORT", color: "var(--color-service-transport)" },
  { name: "TOUR", color: "var(--color-service-tour)" },
  { name: "UMRAH", color: "var(--color-service-umrah)" },
  { name: "RAIL", color: "var(--color-service-flight)" },
  { name: "FLIGHT", color: "var(--color-service-flight)" },
  { name: "OTHERS", color: "var(--color-service-others)" },
];

const getServiceColor = (service) => {
  const found = serviceTypes.find((s) => s.name === service);
  return found ? found.color : "var(--color-service-others)";
};

/**
 * Core filter function — applies text search AND date range together.
 * Call this whenever either filter value changes.
 */
const applyFilters = (allTransactions, text, fromDate, toDate) => {
  let result = allTransactions;

  // --- Date filter ---
  if (fromDate || toDate) {
    const from = fromDate ? new Date(fromDate) : null;
    const to = toDate ? new Date(toDate) : null;
    if (to) to.setHours(23, 59, 59, 999); // include full end-of-day

    result = result.filter((item) => {
      const d = new Date(item.createdDateTime);
      if (from && d < from) return false;
      if (to && d > to) return false;
      return true;
    });
  }

  // --- Text search filter ---
  const q = text.trim().toLowerCase();
  if (q) {
    result = result.filter((item) =>
      [
        item.referenceId,
        item.transactionType,
        item.transactionCategory,
        item.service,
        item.narration,
        item.remarks,
        item.ledgerType,
        item.b2bUser?.identificationShortCode,
        item.b2bUser?.companyName,
        item.vendor?.identificationShortCode,
        item.vendor?.vendorName,
        String(item.debit ?? ""),
        String(item.credit ?? ""),
        String(item.creditUsed ?? ""),
        String(item.currentBalance ?? ""),
        String(item.creditBalance ?? ""),
      ]
        .filter(Boolean)
        .some((val) => val.toLowerCase().includes(q))
    );
  }

  return result;
};

const calcTotals = (rows) => ({
  totalDebit: rows.reduce((s, r) => s + Number(r.debit ?? 0), 0),
  totalCredit: rows.reduce((s, r) => s + Number(r.credit ?? 0), 0),
  totalCreditUsed: rows.reduce((s, r) => s + Number(r.creditUsed ?? 0), 0),
});

/**
 * Vendor / User grouped totals — debit, credit, creditUsed, and net "amount"
 * (credit + creditUsed - debit) per ledgerType group.
 * NOTE: adjust the +creditUsed sign here if creditUsed should reduce
 * the amount instead of adding to it (see conversation notes).
 */
const calcLedgerTypeTotals = (rows) => {
  let vendorDebit = 0, vendorCredit = 0, vendorCreditUsed = 0;
  let userDebit = 0, userCredit = 0, userCreditUsed = 0;

  rows.forEach((r) => {
    const type = (r.ledgerType || "").toUpperCase();
    const debit = Number(r.debit ?? 0);
    const credit = Number(r.credit ?? 0);
    const creditUsed = Number(r.creditUsed ?? 0);

    if (type.includes("VENDOR")) {
      vendorDebit += debit;
      vendorCredit += credit;
      vendorCreditUsed += creditUsed;
    } else if (type.includes("USER")) {
      userDebit += debit;
      userCredit += credit;
      userCreditUsed += creditUsed;
    }
  });

  return {
    totalVendorDebit: vendorDebit,
    totalVendorCredit: vendorCredit,
    totalVendorCreditUsed: vendorCreditUsed,
    totalVendorAmount: vendorCreditUsed + vendorDebit,

    totalUserDebit: userDebit,
    totalUserCredit: userCredit,
    totalUserCreditUsed: userCreditUsed,
    totalUserAmount: userCreditUsed + userDebit,
  };
};

const HajjPackageLedger = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const id = location?.state?.id;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const REMOTE_SALE = process.env.REACT_APP_INVENTORY_REMOTE_SALE;

  // --- Source data (never mutated after fetch) ---
  const [allTransactions, setAllTransactions] = useState([]);
  const [ledgerData, setLedgerData] = useState(null);
  const [documentName, setDocumentName] = useState("");

  // --- Filter state ---
  const [searchInput, setSearchInput] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [showFilters, setShowFilters] = useState(false);

  // --- Derived / display state ---
  const [displayRows, setDisplayRows] = useState([]);
  const [totals, setTotals] = useState({ totalDebit: 0, totalCredit: 0, totalCreditUsed: 0 });
  const [ledgerTypeTotals, setLedgerTypeTotals] = useState({
    totalVendorDebit: 0, totalVendorCredit: 0, totalVendorCreditUsed: 0, totalVendorAmount: 0,
    totalUserDebit: 0, totalUserCredit: 0, totalUserCreditUsed: 0, totalUserAmount: 0,
  });

  // --- Pagination ---
  const [limit, setLimit] = useState(20);
  const [pageNumber, setPageNumber] = useState(1);

  // --- UI ---
  const [anchorElPrint, setAnchorElPrint] = useState(null);
  const [fileModal, setFileModal] = useState(false);
  const [documentUrl, setDocumentUrl] = useState("");
  const [activeRemarkRowId, setActiveRemarkRowId] = useState(null);
 
  useEffect(() => {
    const fetchData = async () => {
      try {
        const { data } = await getHajjPackage(id);
        const booking = data?.payload;
        const txns = booking?.transactions || [];

        setLedgerData(booking);
        setAllTransactions(txns);
        setPageNumber(1);
      } catch (error) {
        console.error(error);
        errorMsg("Failed to load Hajj Package.");
      }
    };
    fetchData();
  }, [id]);

  // ---------------------------------------------------------------------------
  // Running Cash Balance — computed chronologically (oldest -> newest),
  // then remapped back to the original (newest-first) display order.
  //
  // NOTE: this assumes `allTransactions` is returned newest-first by the API.
  // If the API ever returns oldest-first, remove the two .reverse() calls below.
  // ---------------------------------------------------------------------------

  const transactionsWithBalance = useMemo(() => {
    const chronological = [...allTransactions].reverse();

    let cumulativeUserPayment = 0;
    let cumulativeVendorPayment = 0;

    const withBalance = chronological.map((row) => {
      const type = (row.ledgerType || "").toUpperCase();
      
      const amount =
        Number(row.debit ?? 0) +
        Number(row.creditUsed ?? 0) -
        Number(row.credit ?? 0);

      if (type.includes("USER")) {
        cumulativeUserPayment += amount;
      } else if (type.includes("VENDOR")) {
        cumulativeVendorPayment += amount;
      }

      return {
        ...row,
        computedCashBalance: cumulativeUserPayment - cumulativeVendorPayment,
      };
    });

    return withBalance.reverse(); // back to newest-first for display
  }, [allTransactions]);

  // ---------------------------------------------------------------------------
  // Unified filter effect — runs whenever any filter value OR source data changes
  // ---------------------------------------------------------------------------

  useEffect(() => {
    const filtered = applyFilters(transactionsWithBalance, searchInput, fromDate, toDate);
    setDisplayRows(filtered);
    setTotals(calcTotals(filtered));
    setLedgerTypeTotals(calcLedgerTypeTotals(filtered));
    setPageNumber(1); // reset to page 1 on every filter change
  }, [searchInput, fromDate, toDate, transactionsWithBalance]);

  // ---------------------------------------------------------------------------
  // Pagination slice
  // ---------------------------------------------------------------------------

  const pagedRows = displayRows.slice((pageNumber - 1) * limit, pageNumber * limit);
  const pageCount = Math.max(1, Math.ceil(displayRows.length / limit));

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  const handleSearch = (e) => setSearchInput(e.target.value);

  const handleResetFilter = () => {
    setSearchInput("");
    setFromDate("");
    setToDate("");
    setShowFilters(false);
  };

  const handlePageChange = (_e, value) => setPageNumber(value);
  const handlePageView = (value) => { setLimit(Number(value)); setPageNumber(1); };

  const handleFileClick = (scanCopyUrl) => {
    if (scanCopyUrl) {
      setDocumentUrl(scanCopyUrl);
      setFileModal(true);
    } else {
      Swal.fire({ title: "No Document", text: "No scanned copy available.", icon: "info", confirmButtonText: "OK" });
    }
  };

  const handlePrintClick = (e) => setAnchorElPrint(e.currentTarget);
  const handlePrintMenuClose = () => setAnchorElPrint(null);

  const handleExportExcel = async () => {
    if (!displayRows.length) {
      Swal.fire({ title: "No Data", text: "No records to export.", icon: "info", confirmButtonText: "OK" });
      return;
    }
    try {
      const exportData = displayRows.map((entry, idx) => ({
        "Transaction Date": entry.createdDateTime ? moment(entry.createdDateTime).format("DD-MMM-YYYY hh:mm A") : "N/A",
        "Reference ID": entry.referenceId || "N/A",
        "Type": entry.transactionType || "N/A",
        "Service": entry.service || "N/A",
        "Narration": entry.narration || "N/A",
        "Cash Used": commaNumber(parseFloat(entry.debit ?? 0).toFixed(2)),
        "Balance": commaNumber(parseFloat(entry.computedCashBalance ?? 0).toFixed(2)),
        "Credit Used": commaNumber(parseFloat(entry.creditUsed ?? 0).toFixed(2)),
        "Remarks": entry.remarks || "N/A",
      }));

      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Hajj Ledger");

      const headers = Object.keys(exportData[0]);
      worksheet["!cols"] = headers.map((h) => ({
        wch: Math.max(h.length + 2, ...exportData.map((r) => String(r[h] || "").length + 2)),
      }));

      XLSX.writeFile(workbook, `HajjLedger_${moment().format("YYYYMMDD_HHmm")}.xlsx`);
      successAlert("Exported successfully!");
    } catch (err) {
      console.error(err);
      errorAlert("Export failed. Please try again.");
    }
  };

  // ---------------------------------------------------------------------------
  // Render helpers
  // ---------------------------------------------------------------------------

  const formatTransactionType = (type) => {
    if (!type) return "";
    if (type === "CL_ADD") return "CL Add";
    if (type === "CL_CLEAR") return "CL Clear";
    if (type === "MFS") return "MFS";
    return type.toLowerCase().replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const getReferenceColor = (row) => {
    const { transactionCategory: cat, transactionType: type } = row;
    if ((cat === "DEPOSIT" && type !== "ADD") || cat === "REFUND" || cat === "CASHBACK") return "var(--color-paid)";
    if ((cat === "PAYMENT" && type !== "DEDUCT") || cat === "CREDIT_SETTLE") return "var(--color-danger)";
    if (cat === "CREDIT_ADJUST") return "var(--color-info)";
    if (cat === "CREDIT_USE") return "var(--color-warning)";
    if (type === "DEDUCT" || type === "ADD") return "var(--color-draft)";
    return "var(--color-gray-750)";
  };

  const renderNarration = (row) => {
    if (!row.narration) return null;
    const firstSeven = row.narration.substring(0, 7);
    const remaining = row.narration.substring(7);
    const hyphenIdx = remaining.indexOf("-");
    const underlinedText = hyphenIdx !== -1 ? firstSeven + remaining.substring(0, hyphenIdx) : firstSeven + remaining;
    const bookingId = hyphenIdx !== -1 ? remaining.substring(0, hyphenIdx).trim() : remaining.trim();
    const afterHyphen = hyphenIdx !== -1 ? remaining.substring(hyphenIdx) : "";
    const isClickable = bookingId && row.transactionType === "ISSUE" && row.service === "FLIGHT";

    if (isClickable) {
      return (
        <>
          <span
            style={{ cursor: "pointer", textDecoration: "underline", textDecorationColor: "var(--color-primary)", color: "var(--color-primary)" }}
            onClick={() => navigate(`/dashboard/airbooking-details/${bookingId}`)}
          >
            {underlinedText}
          </span>
          <span>{afterHyphen}</span>
        </>
      );
    }
    return <><span>{underlinedText}</span><span>{afterHyphen}</span></>;
  };

  const renderBaseAmount = (row, field) =>
    REMOTE_SALE === "true" && row.baseAmount && (+row[field] || 0) > 0 && row.baseCurrency && row.baseRate ? (
      <div style={{ fontSize: "0.85em", color: "var(--badge-group-table-cell-3)", marginTop: "4px" }}>
        {row.baseCurrency} {row.baseAmount} * {row.baseRate}
      </div>
    ) : null;

  const renderLedgerParty = (row) => {
    if (row.b2bUser?.identificationShortCode)
      return `${row.b2bUser.identificationShortCode} - ${row.b2bUser.companyName}`;
    if (row.vendor?.identificationShortCode)
      return `${row.vendor.identificationShortCode} - ${row.vendor.vendorName}`;
    return "";
  };

  // ---------------------------------------------------------------------------
  // JSX
  // ---------------------------------------------------------------------------

  return (
    <Box sx={{ backgroundColor: "var(--color-bg-white)", padding: "16px", borderRadius: "12px", marginTop: { xs: "20px", sm: "0" } }}>

      {/* ── Top bar ── */}
      <Box
        mt={2} mb={1}
        sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, justifyContent: "space-between", alignItems: { xs: "flex-start", sm: "center" }, gap: { xs: 2, sm: 0 }, width: "100%" }}
      >
        {/* Left controls */}
        <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, alignItems: "center", gap: { xs: 2, sm: "12px" }, width: "100%" }}>

          {/* Search */}
          <Box sx={{ position: "relative", width: { xs: "100%", sm: "auto" } }}>
            <Search style={{ position: "absolute", top: "7px", left: "5px", color: "var(--color-draft)", zIndex: 1 }} />
            <CustomSearchInput
              value={searchInput}
              onChange={handleSearch}
              type="text"
              placeholder="Quick Search"
              style={{ padding: "0px", boxSizing: "border-box", backgroundColor: "var(--color-bg-hover-sitebar)" }}
            />
          </Box>

          {/* View */}
          <Box sx={{ display: "flex", alignItems: "center", backgroundColor: "var(--color-bg-hover-sitebar)", borderRadius: "4px", padding: "5px", width: { xs: "100%", sm: "auto" } }}>
            <Typography sx={{ fontFamily: "Outfit", fontWeight: 400, fontSize: "14px", color: "var(--color-draft)", paddingRight: "10px" }}>View</Typography>
            <CustomSelect onChange={(e) => handlePageView(e.target.value)} defaultValue={20} sx={{ backgroundColor: "var(--color-bg-white)", height: "25px", border: "none", color: "var(--color-draft)", padding: "0px", cursor: "pointer" }}>
              <MenuItem value={20}>20</MenuItem>
              <MenuItem value={50}>50</MenuItem>
              <MenuItem value={100}>100</MenuItem>
              <MenuItem value={200}>200</MenuItem>
              <MenuItem value={500}>500</MenuItem>
            </CustomSelect>
          </Box>

          {/* Filter toggle */}
          <Box sx={{ display: "flex", alignItems: "center", backgroundColor: "var(--color-bg-hover-sitebar)", borderRadius: "4px", padding: "5px", cursor: "pointer", width: { xs: "100%", sm: "auto" } }} onClick={() => setShowFilters((p) => !p)}>
            <Typography sx={{ fontFamily: "Outfit", fontWeight: 400, fontSize: "14px", color: "var(--color-draft)", paddingRight: "10px" }}>Filter</Typography>
            <FilterList sx={{ color: "var(--color-draft)", backgroundColor: "var(--color-bg-white)", padding: "2px 4px", fontSize: "22px", borderRadius: "5px" }} />
          </Box>

          {/* Reset */}
          <Box sx={{ display: "flex", alignItems: "center", backgroundColor: "var(--color-bg-hover-sitebar)", borderRadius: "4px", padding: "5px", cursor: "pointer", width: { xs: "100%", sm: "auto" } }} onClick={handleResetFilter}>
            <Typography sx={{ fontFamily: "Outfit", fontWeight: 400, fontSize: "14px", color: "var(--color-draft)", paddingRight: "10px" }}>Reset</Typography>
            <img src={ResetIcon} alt="Reset" style={{ width: "16px", height: "16px", backgroundColor: "var(--color-bg-white)", padding: "6px", borderRadius: "5px" }} />
          </Box>
        </Box>

        {/* Date filters (visible when showFilters is true) */}
        {showFilters && (
          <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, alignItems: "center", marginLeft: "10px", gap: { xs: 2, sm: "12px" }, width: "100%" }}>
            <CustomInput
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              sx={{ backgroundColor: "var(--color-bg-hover-sitebar)", borderRadius: "4px" }}
            />
            <CustomInput
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              sx={{ backgroundColor: "var(--color-bg-hover-sitebar)", borderRadius: "4px" }}
            />
          </Box>
        )}

        {/* Print / Export */}
        <Box sx={{ display: "flex", gap: "12px", alignItems: "center", justifyContent: { xs: "space-between", sm: "flex-end" }, width: "100%" }}>
          <IconButton onClick={handlePrintClick}>
            <img src={PrintIcon} alt="Print" style={{ height: "37px", cursor: "pointer" }} />
          </IconButton>
          <Menu anchorEl={anchorElPrint} keepMounted open={Boolean(anchorElPrint)} onClose={handlePrintMenuClose}>
            <MuiMenuItem onClick={handleExportExcel}>Export to Excel</MuiMenuItem>
          </Menu>
        </Box>
      </Box>

      {/* ── Active filter badge ── */}
      {(searchInput || fromDate || toDate) && (
        <Box sx={{ display: "flex", alignItems: "center", gap: "8px", mb: 1, flexWrap: "wrap" }}>
          <Typography sx={{ fontFamily: "Outfit", fontSize: "12px", color: "var(--color-draft)" }}>
            Showing {displayRows.length} of {allTransactions.length} records
            {searchInput && <> matching <strong>"{searchInput}"</strong></>}
            {fromDate && <> from <strong>{fromDate}</strong></>}
            {toDate && <> to <strong>{toDate}</strong></>}
          </Typography>
          <Button size="small" variant="text" onClick={handleResetFilter} sx={{ fontFamily: "Outfit", fontSize: "11px", textTransform: "none", color: "var(--color-danger)" }}>
            Clear filters
          </Button>
        </Box>
      )}

      {/* ── Info bar ── */}
      <Box sx={{ gap: "16px", display: "grid", fontSize: "12px", borderRadius: "4px", padding: "10px 5px", marginBottom: "10px", fontFamily: "Outfit", backgroundColor: "var(--color-bg-hover-sitebar)", gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" } }}>
        <span>Package ID: {ledgerData?.hajjPackageId}</span>
        <span>Package Name: {ledgerData?.hajjPackageName}</span>
        <span>Total Persons: {ledgerData?.totalPerson}</span>
      </Box>

      {/* ── Vendor / User totals bar ── */}
      <Box sx={{ gap: "16px", display: "grid", fontSize: "12px", borderRadius: "4px", padding: "10px 5px", marginBottom: "10px", fontFamily: "Outfit", backgroundColor: "var(--color-bg-hover-sitebar)", gridTemplateColumns: { xs: "2fr", sm: "repeat(3, 2fr)" } }}>
        <span>
          Total Vendor Amount: <strong>{commaNumber(ledgerTypeTotals.totalVendorAmount.toFixed(2))}</strong>
        </span>
        <span>
          Total User Amount: <strong>{commaNumber(ledgerTypeTotals.totalUserAmount.toFixed(2))}</strong>
        </span>

        <span>
          Total Profit/Loss: <strong>{commaNumber(ledgerTypeTotals.totalUserAmount - ledgerTypeTotals.totalVendorAmount)}</strong>
        </span>
      </Box>

      {/* ── Table ── */}
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <CustomTableHeaderCell group="first">Transaction Date</CustomTableHeaderCell>
              <CustomTableHeaderCell group="first">Ledger</CustomTableHeaderCell>
              <CustomTableHeaderCell group="first">Ledger Party</CustomTableHeaderCell>
              <CustomTableHeaderCell group="first">Reference ID</CustomTableHeaderCell>
              <CustomTableHeaderCell group="first">Type</CustomTableHeaderCell>
              <CustomTableHeaderCell group="first">Service</CustomTableHeaderCell>
              <CustomTableHeaderCell group="first" style={{ width: "250px" }}>Narration</CustomTableHeaderCell>
              <CustomTableHeaderCell group="second">Cash Used</CustomTableHeaderCell>
              <CustomTableHeaderCell group="second">Balance</CustomTableHeaderCell>
              <CustomTableHeaderCell group="third">Credit Used</CustomTableHeaderCell>
              <CustomTableHeaderCell sx={{ textAlign: "right" }}>Action</CustomTableHeaderCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {pagedRows.length === 0 ? (
              <TableRow>
                <CustomTableCell colSpan={12} sx={{ textAlign: "center", padding: "32px", color: "var(--color-gray)" }}>
                  No records found
                  {(searchInput || fromDate || toDate) && (
                    <> — <span style={{ cursor: "pointer", textDecoration: "underline" }} onClick={handleResetFilter}>clear filters</span></>
                  )}
                </CustomTableCell>
              </TableRow>
            ) : (
              pagedRows.map((row, index) => {
                const serialNo = (pageNumber - 1) * limit + index + 1; // continuous serial across pages
                return (
                  <TableRow key={row.id ?? index} sx={{ borderBottom: "2px solid var(--color-gray-500)" }}>

                    {/* Transaction Date */}
                    <CustomTableCell>
                      {moment(row.createdDateTime).format("DD-MMM-YYYY hh:mm A")}
                    </CustomTableCell>

                    {/* Ledger */}
                    <CustomTableCell>{row.ledgerType}</CustomTableCell>

                    {/* Ledger Party */}
                    <CustomTableCell>{renderLedgerParty(row)}</CustomTableCell>

                    {/* Reference ID */}
                    <CustomTableCell>
                      <Tooltip title={row.transactionCategory || ""} arrow>
                        <span style={{ padding: "5px", color: "var(--color-bg-white)", borderRadius: "2px", backgroundColor: getReferenceColor(row) }}>
                          {row.referenceId}
                        </span>
                      </Tooltip>
                    </CustomTableCell>

                    {/* Type */}
                    <CustomTableCell>{formatTransactionType(row.transactionType)}</CustomTableCell>

                    {/* Service */}
                    <CustomTableCell>
                      {row.service && !["wallet", "all"].includes(row.service?.toLowerCase()) && (
                        <Box display="flex" alignItems="center" justifyContent="center" sx={{ backgroundColor: "var(--Boxider-bg)", padding: "5px", borderRadius: "2px" }}>
                          <Box component="span" sx={{ width: "6px", height: "6px", borderRadius: "50%", backgroundColor: getServiceColor(row.service), marginRight: "8px" }} />
                          {row.service}
                        </Box>
                      )}
                    </CustomTableCell>

                    {/* Narration */}
                    <CustomTableCell style={{ width: "250px" }}>{renderNarration(row)}</CustomTableCell>

                    {/* Debit */}
                    <CustomTableCell>
                      {(+row.debit || 0).toFixed(2)}
                      {renderBaseAmount(row, "debit")}
                    </CustomTableCell>

                    {/* Cash Balance — computed running balance (USER payments - VENDOR payments), serial-wise */}
                    <CustomColoredCell column="cashBalance">
                      {commaNumber(Number(row.computedCashBalance ?? 0).toFixed(2))}
                    </CustomColoredCell>

                    {/* Credit Used */}
                    <CustomTableCell>
                      {(+row.creditUsed || 0).toFixed(2)}
                      {renderBaseAmount(row, "creditUsed")}
                    </CustomTableCell>

                    {/* Action */}
                    <TableCell align="right" sx={{ padding: "6px !important", verticalAlign: "middle" }}>
                      {row.scanCopyUrl && (
                        <IconButton onClick={() => handleFileClick(row.scanCopyUrl)} sx={{ padding: 0 }}>
                          <AttachFileIcon sx={{ fontSize: "16px", color: "var(--color-draft)" }} />
                        </IconButton>
                      )}

                      {isMobile ? (
                        <>
                          <IconButton sx={{ color: "var(--color-draft)", width: "10px", marginLeft: "5px" }} onClick={() => setActiveRemarkRowId((p) => (p === row.id ? null : row.id))}>
                            <InfoIcon sx={{ fontSize: "16px !important" }} />
                          </IconButton>
                          {activeRemarkRowId === row.id && (
                            <Box sx={{ position: "absolute", right: 0, backgroundColor: "var(--color-white)", border: "1px solid var(--color-gray-750)", borderRadius: "4px", padding: "6px 8px", fontSize: "12px", maxWidth: "200px", zIndex: 10 }}>
                              {row.remarks || "N/A"}
                            </Box>
                          )}
                        </>
                      ) : (
                        <Tooltip title={row.remarks || "N/A"} arrow>
                          <span>
                            <IconButton sx={{ color: "var(--color-draft)", width: "10px", marginLeft: "5px" }}>
                              <InfoIcon sx={{ fontSize: "16px !important" }} />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* ── Pagination ── */}
      <Box sx={{ width: "100%", my: 3, display: "flex", justifyContent: "center" }}>
        <Stack spacing={2}>
          <Pagination
            count={pageCount}
            page={pageNumber}
            onChange={handlePageChange}
            sx={{
              ".MuiPaginationItem-root": { borderRadius: "50%" },
              ".Mui-selected": { backgroundColor: "var(--color-primary-light) !important", color: "var(--color-bg-white) !important" },
            }}
          />
        </Stack>
      </Box>

      <Modal
        open={fileModal}
        onClose={() => setFileModal(false)}
        BackdropProps={{
          style: {
            backgroundColor: "transparent",
          },
        }}
      >
        <Box sx={{ ...modalStyle }}>
          <Box
            sx={{
              position: "relative",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <IconButton
              onClick={() => setFileModal(false)}
              sx={{
                position: "absolute",
                top: "10px",
                right: "10px",
                backgroundColor: "var(--color-gray)",
                color: "var(--color-bg-white)",
                "&:hover": {
                  backgroundColor: "var(--color-gray)",
                },
              }}
            >
              <CloseIcon />
            </IconButton>

            <img
              src={documentUrl}
              alt={documentName}
              style={{
                borderRadius: "20px",
                maxHeight: "80vh",
                maxWidth: "80vw",
                objectFit: "contain",
              }}
            />

            <IconButton
              onClick={() => window.open(documentUrl, "_blank")}
              sx={{
                position: "absolute",
                bottom: "10px",
                right: "10px",
                backgroundColor: "var(--color-primary-dark)",
                color: "var(--color-bg-white)",
                borderRadius: "5px",
                padding: "5px",
                "&:hover": { backgroundColor: "var(--color-primary-dark)" },
              }}
            >
              <GetAppIcon />
              <Typography sx={{ ml: 1 }}>Download</Typography>
            </IconButton>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default HajjPackageLedger;
